import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/components/ui/use-toast'
import { Trash2, RefreshCw, Bug, CheckCircle, XCircle } from 'lucide-react'

const AuthDebug = () => {
	const { toast } = useToast()
	const [debugInfo, setDebugInfo] = useState<any>({})
	const [isChecking, setIsChecking] = useState(false)

	const clearAuthCache = () => {
		const keysToRemove: string[] = []

		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i)
			if (
				key &&
				(key.includes('supabase') ||
					key.includes('auth-token') ||
					key.includes('oauth') ||
					key.includes('code-verifier') ||
					key.includes('shared-sb-auth-token'))
			) {
				keysToRemove.push(key)
			}
		}

		keysToRemove.forEach((key) => {
			localStorage.removeItem(key)
		})

		toast({
			title: 'Cache Cleared',
			description: `Removed ${keysToRemove.length} auth-related items from localStorage`,
		})

		setDebugInfo((prev) => ({ ...prev, clearedItems: keysToRemove }))
	}

	const checkAuthStatus = async () => {
		setIsChecking(true)
		const info: any = {
			timestamp: new Date().toISOString(),
			currentUrl: window.location.href,
			origin: window.location.origin,
			expectedCallbackUrl: `${window.location.origin}/auth/callback`,
		}

		try {
			// Check current session
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession()
			info.hasSession = !!session
			info.sessionError = sessionError?.message

			if (session) {
				info.userId = session.user.id
				info.userEmail = session.user.email
				info.sessionExpiresAt = new Date(
					session.expires_at! * 1000
				).toISOString()
			}

			// Check localStorage for OAuth remnants
			const oauthKeys: string[] = []
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i)
				if (key && (key.includes('oauth') || key.includes('code-verifier'))) {
					oauthKeys.push(key)
				}
			}
			info.oauthKeysInStorage = oauthKeys

			// Test OAuth configuration
			info.supabaseUrl = (supabase as any).supabaseUrl || 'Not accessible'
			info.supabaseAnonKey = (supabase as any).supabaseKey
				? `${(supabase as any).supabaseKey.substring(0, 20)}...`
				: 'Not accessible'
		} catch (error: any) {
			info.checkError = error.message
		}

		setDebugInfo(info)
		setIsChecking(false)
	}

	const testOAuthRedirect = () => {
		// Create a test OAuth URL to see what Supabase generates
		const testUrl = `${window.location.origin}/auth/callback`

		toast({
			title: 'OAuth Redirect Test',
			description: (
				<div className="space-y-1 text-xs">
					<p>Expected redirect URL:</p>
					<code className="block bg-black/10 p-1 rounded">{testUrl}</code>
					<p className="mt-2">
						This URL must be in Supabase Dashboard → Auth → URL Configuration
					</p>
				</div>
			),
			duration: 10000,
		})
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
			<Card className="w-full max-w-2xl">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Bug className="h-5 w-5" />
						OAuth Authentication Debugger
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex gap-2 flex-wrap">
						<Button onClick={checkAuthStatus} disabled={isChecking}>
							<RefreshCw
								className={`mr-2 h-4 w-4 ${isChecking ? 'animate-spin' : ''}`}
							/>
							Check Auth Status
						</Button>
						<Button onClick={clearAuthCache} variant="destructive">
							<Trash2 className="mr-2 h-4 w-4" />
							Clear Auth Cache
						</Button>
						<Button onClick={testOAuthRedirect} variant="outline">
							Test OAuth URL
						</Button>
					</div>

					{Object.keys(debugInfo).length > 0 && (
						<div className="space-y-3 mt-4">
							<h3 className="font-semibold text-sm">Debug Information:</h3>
							<div className="bg-gray-100 rounded-lg p-4 text-xs font-mono space-y-2">
								{Object.entries(debugInfo).map(([key, value]) => (
									<div key={key} className="flex items-start gap-2">
										<span className="text-gray-600 min-w-[140px]">{key}:</span>
										<span className="text-gray-900 break-all">
											{typeof value === 'boolean' ? (
												value ? (
													<span className="flex items-center gap-1 text-green-600">
														<CheckCircle className="h-3 w-3" /> true
													</span>
												) : (
													<span className="flex items-center gap-1 text-red-600">
														<XCircle className="h-3 w-3" /> false
													</span>
												)
											) : Array.isArray(value) ? (
												value.length > 0 ? (
													value.join(', ')
												) : (
													'[]'
												)
											) : (
												String(value)
											)}
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					<div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
						<h4 className="font-semibold text-sm mb-2">
							⚠️ Required Supabase Configuration:
						</h4>
						<ol className="text-xs space-y-1 list-decimal list-inside">
							<li>
								Go to Supabase Dashboard → Authentication → URL Configuration
							</li>
							<li>
								Add this EXACT URL (no wildcards):{' '}
								<code className="bg-yellow-100 px-1">
									{window.location.origin}/auth/callback
								</code>
							</li>
							<li>Save changes</li>
							<li>Clear cache using button above</li>
							<li>Try logging in again</li>
						</ol>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

export default AuthDebug
